/*
 * The chat reducer: one pure function from (model, event) to a new model and a verdict, exhaustive
 * over `ChatEvent` (protocol.md §5.2). It never repeats the gap rule: core's `applyEvent` has
 * already gated the event by `eventId` and, for a seq-allocating one, against the conversation's
 * dense counter (REUSE C5), so a reducer case that runs is an event that belongs.
 *
 * Identity is the other rule: an untouched conversation, message window or message object comes out
 * of the reducer as the same object, so a memoised row does not re-render because a different
 * conversation moved.
 *
 * Two windows are kept, not one: the conversation's timeline in `model.messages` and, in the
 * module's own slice, the threads a pane has opened. A reply belongs to its thread (UX §4.1 R1) and
 * its root to both, which is why a patch runs over both — and a reply whose sender ticked "Also send
 * to #channel" belongs to both as well (X-A1 A4), which is why the arrival's two destinations are
 * two independent questions rather than an either/or.
 */

import { withConversation } from "../../../core/store/applyEvent.ts"
import type {
	Applied,
	ConversationId,
	ConversationSlice,
	Model,
	WireEventLike,
} from "../../../core/store/model.ts"
import type { ChatEvent } from "../events.ts"
import type { MemberSelf } from "../wire/chat.gen.ts"
import type { ConversationView, MessageView } from "../wire/views.ts"
import { chatSlice, withChatSlice } from "./selectors.ts"
import type { ChatSlice, ThreadWindow } from "./slice.ts"
import { capReplies, capRows, type Bounds } from "./trim.ts"

/**
 * Replaces the rows of a loaded window and keeps its bounds in step. Core's own `withMessages`
 * takes a whole slice; this one takes the rows, which is what every case here has.
 *
 * `capped` is passed by the one case that makes a window *longer* — the append below. A patch never
 * needs it: it hands back as many rows as it was given, and trimming a window because a reaction
 * arrived would take rows off the screen the reader is looking at.
 */
function withRows(model: Model, conv: ConversationId, rows: MessageView[], capped?: Bounds): Model {
	const window = model.messages[conv]
	if (!window) return model
	const first = rows[0]
	const last = rows[rows.length - 1]
	return {
		...model,
		messages: {
			...model.messages,
			[conv]: {
				...window,
				...capped,
				rows,
				firstSeq: first ? first.seq : window.firstSeq,
				lastSeq: last ? last.seq : window.lastSeq,
			},
		},
	}
}

/**
 * Replaces one loaded message wherever it is loaded; a message on neither side is not an error.
 * A message lives in the conversation's window or in a thread's replies, never in both — but a
 * thread's *root* is in both at once, which is why every patch runs over both.
 */
function patchMessage(
	model: Model,
	conv: ConversationId,
	id: number,
	patch: (msg: MessageView) => MessageView,
): Model {
	return patchThreads(patchWindow(model, conv, id, patch), id, patch)
}

/** The conversation's own timeline. */
function patchWindow(
	model: Model,
	conv: ConversationId,
	id: number,
	patch: (msg: MessageView) => MessageView,
): Model {
	const rows = model.messages[conv]?.rows as MessageView[] | undefined
	if (!rows) return model
	const index = rows.findIndex((m) => m.id === id)
	if (index < 0) return model
	const next = rows.slice()
	next[index] = patch(rows[index]!)
	return withRows(model, conv, next)
}

/** Every loaded thread: its root and its replies, each untouched window kept identical. */
function patchThreads(model: Model, id: number, patch: (msg: MessageView) => MessageView): Model {
	const slice = chatSlice(model)
	let threads: Record<number, ThreadWindow> | undefined
	for (const [key, window] of Object.entries(slice.threads)) {
		let next = window
		if (window.root?.id === id) next = { ...next, root: patch(window.root) }
		const index = window.rows.findIndex((m) => m.id === id)
		if (index >= 0) {
			const rows = window.rows.slice()
			rows[index] = patch(window.rows[index]!)
			next = { ...next, rows }
		}
		if (next !== window) {
			threads ??= { ...slice.threads }
			threads[Number(key)] = next
		}
	}
	return threads === undefined ? model : withChatSlice(model, { ...slice, threads })
}

/** Forgets every loaded thread: a resync rebuilds what they were read against (UX §4.6). */
export function clearThreads(model: Model): Model {
	const slice = chatSlice(model)
	if (Object.keys(slice.threads).length === 0) return model
	return withChatSlice(model, { ...slice, threads: {} })
}

function conversationSlice(model: Model, conv: ConversationId): ConversationSlice | undefined {
	return model.conversations[conv]
}

/**
 * Writes one conversation's row into the model, whether it is new or already there, and puts it in
 * `conversationOrder`. Exported because a join answers with the row over HTTP and the sidebar must
 * not wait for the socket to repeat it (`JoinBanner`).
 *
 * `conversationOrder` is the list of conversations this reader **is in** — the sidebar's order, and
 * what "/" lands in. A conversation the reader is merely reading goes in through
 * `upsertBrowsedConversation` instead, and a membership that stops being active takes its entry
 * out again (`chat.membership.self` below).
 */
export function upsertConversation(model: Model, view: ConversationView): Model {
	const listed = model.conversationOrder.includes(view.id)
		? model.conversationOrder
		: [...model.conversationOrder, view.id]
	return { ...upsertBrowsedConversation(model, view), conversationOrder: listed }
}

/**
 * A head update as this reader keeps it. The server renders one view of the conversation for all its
 * members, as the person whose write it was sees it (`emitConversationUpdated`), and a direct
 * conversation's `dmHandle` is relative: it is the route segment of the *other* people, so the writer's
 * view carries the reader's own handle. A change that leaves the member set as it was — a switch for
 * AI chats, a rename — leaves the reader's address as it was, so the reader keeps the one it holds;
 * without that, the conversation the reader has open stopped answering to its own address the moment
 * the other person changed it.
 */
function withReadersAddress(model: Model, view: ConversationView): ConversationView {
	const held = model.conversations[view.id]?.meta as ConversationView | undefined
	if (held?.dmHandle === undefined || held.dmHandle === view.dmHandle) return view
	const before = [...(held.memberIds ?? [])].sort((a, b) => a - b)
	const after = [...(view.memberIds ?? [])].sort((a, b) => a - b)
	const same = before.length === after.length && before.every((id, i) => id === after[i])
	return same ? { ...view, dmHandle: held.dmHandle } : view
}

/**
 * The same row without a place in the sidebar's order: a public channel a reader has never been in
 * reaches the model over HTTP and by its address alone (`history.ts` `loadPublicChannel`). It is
 * readable, so the model must hold it; the reader is not in it, so nothing that lists their own
 * conversations — the sidebar, the quick switcher, the landing redirect of "/" — may offer it.
 */
export function upsertBrowsedConversation(model: Model, view: ConversationView): Model {
	const existing = model.conversations[view.id]
	// Core's `ConversationView` is the shared subset and spells `memberIds` as always present; the
	// wire omits it above 50 members, where the member list is paged over HTTP instead.
	const meta = { ...view, memberIds: view.memberIds ?? [] }
	const next: ConversationSlice = existing
		? { ...existing, meta, lastSeq: Math.max(existing.lastSeq, view.lastSeq) }
		: { meta, status: "cold", lastSeq: view.lastSeq, unread: 0, mentions: 0, justJoined: true }
	return withConversation(model, view.id, next)
}

/**
 * Keeps one conversation as an administrator's outside view (`ChatSlice.outside`): a channel or
 * private channel they manage without being in it. It is never written into `conversations`, which
 * everything that draws a timeline reads as readable.
 */
export function putOutside(model: Model, view: ConversationView): Model {
	const slice = chatSlice(model)
	return withChatSlice(model, { ...slice, outside: { ...slice.outside, [view.id]: view } })
}

/** Forgets one outside view; a conversation that was never one leaves the model as it was. */
export function dropOutside(model: Model, conv: ConversationId): Model {
	const slice = chatSlice(model)
	if (slice.outside[conv] === undefined) return model
	const outside = { ...slice.outside }
	delete outside[conv]
	return withChatSlice(model, { ...slice, outside })
}

/**
 * Writes this reader's own membership row for one conversation. Exported for the same reason as
 * `upsertConversation`: joining a channel answers with the row over HTTP (`JoinBanner`), and the
 * surface reads exactly this field to decide between a composer and the banner — so the answer is
 * applied rather than waited for.
 */
export function upsertSelf(model: Model, conv: ConversationId, self: MemberSelf): Model {
	const slice = chatSlice(model)
	return withChatSlice(model, { ...slice, self: { ...slice.self, [conv]: self } })
}

/** How many faces the thread footer shows; the server fills `replyUserIds` to the same bound. */
const FOOTER_FACES = 5

/**
 * The counters a reply moves on its root. No event carries them — the reply itself is the news
 * (UX §4.1 R4) — so the client raises them on the root wherever it is loaded.
 */
function bumpRoot(msg: MessageView): (root: MessageView) => MessageView {
	return (root) => {
		const next: MessageView = {
			...root,
			replyCount: (root.replyCount ?? 0) + 1,
			lastReplyAt: msg.createdAt,
			lastReplySeq: msg.seq,
		}
		const faces = root.replyUserIds ?? []
		const author = msg.authorId
		if (author !== undefined && !faces.includes(author) && faces.length < FOOTER_FACES) {
			next.replyUserIds = [...faces, author]
		}
		return next
	}
}

/**
 * A message that belongs to the timeline, when the timeline is loaded up to its end. The append is
 * idempotent: a seq the window already holds, or one at or below its last row, is not a second row
 * — a page that landed with the message in it and the socket's own copy of the same message are
 * one message, whichever arrives first (X-A1 D5).
 *
 * The window is the one thing here that grows without an end of its own, so the append is where the
 * ceiling is applied (`trim.ts`): past it the oldest row goes and the window says there is history
 * above it, which is the same sentence it makes after a page that did not reach the beginning.
 */
function appendToWindow(model: Model, msg: MessageView): Model {
	const window = model.messages[msg.conv]
	// A window with newer messages still unloaded is not the tail: a permalink jump left a hole
	// above it, and appending here would fake a continuity that is not there. The conversation's
	// own counter is deliberately not compared with the window's: a reply moves it without ever
	// becoming a row, and a comparison would stop the timeline for good (UX §4.4).
	if (!window || window.hasMoreAfter) return model
	const rows = window.rows as MessageView[]
	if (rows.length > 0 && msg.seq <= window.lastSeq) return model
	if (rows.some((row) => row.seq === msg.seq)) return model
	const capped = capRows([...rows, msg], window, "oldest")
	return withRows(model, msg.conv, capped.rows, {
		hasMoreBefore: capped.hasMoreBefore,
		hasMoreAfter: capped.hasMoreAfter,
	})
}

/**
 * A reply joins its thread when that thread is loaded; a cold one reads it from the server. A
 * window whose page is still in flight takes it too: the page that lands keeps every row newer
 * than its own last one, so a reply that arrived meanwhile is not lost between the two.
 */
function appendReply(model: Model, msg: MessageView, rootId: number): Model {
	const slice = chatSlice(model)
	const window = slice.threads[rootId]
	if (!window || (window.status !== "ready" && window.status !== "loading")) return model
	const capped = capReplies([...window.rows, msg], window.hasMoreBefore)
	return withChatSlice(model, {
		...slice,
		threads: {
			...slice.threads,
			[rootId]: { ...window, rows: capped.rows, hasMoreBefore: capped.hasMoreBefore },
		},
	})
}

/** Records a reply of this thread that the reader has no need to be told about — their own. */
function markThreadSeen(model: Model, rootId: number, seq: number): Model {
	const slice = chatSlice(model)
	if ((slice.threadSeen[rootId] ?? 0) >= seq) return model
	return withChatSlice(model, { ...slice, threadSeen: { ...slice.threadSeen, [rootId]: seq } })
}

/**
 * One's own message is never below the "New messages" line (A-11 item 7): the divider's anchor
 * follows the seq just sent. Only an anchor that exists moves — a conversation nobody opened has
 * none, and pinning one here would draw a divider on the next visit.
 */
function anchorOwnMessage(model: Model, msg: MessageView): Model {
	const slice = chatSlice(model)
	const anchor = slice.unreadAnchor[msg.conv]
	if (anchor === undefined || anchor >= msg.seq) return model
	return withChatSlice(model, {
		...slice,
		unreadAnchor: { ...slice.unreadAnchor, [msg.conv]: msg.seq },
	})
}

/**
 * The arrival of a message, in three independent steps (UX §4.4). The conversation's dense counter
 * always moves, a reply included, so core's gap rule keeps working; the unread badge counts only
 * what the timeline will show; and the row goes either into the loaded window or into the loaded
 * thread of its root, whose counters are patched either way.
 */
function applyNewMessage(model: Model, msg: MessageView): { next: Model; result: Applied } {
	const conv = conversationSlice(model, msg.conv)
	if (!conv) return { next: model, result: { resync: "all" } }

	const mine = model.me?.id === msg.authorId
	const rootId = msg.threadRootId ?? 0
	// A reply its sender also sent to the conversation is a row of the timeline as well as of its
	// thread (X-A1 A4), and the server counts it like any other message — so the client does too.
	const inTimeline = rootId === 0 || msg.alsoToChannel === true
	// Every other reply is outside the badge: the read cursor follows the timeline, which never
	// reaches such a reply's seq, so a counted one could never be cleared again (UX §4.1 R2).
	const bumped: ConversationSlice = {
		...conv,
		lastSeq: Math.max(conv.lastSeq, msg.seq),
		// A system row is not news the way a message is, and the server does not count it either
		// (`counts_unread = 0`): counting it here made a channel bold until the next reload.
		unread: mine || !inTimeline || msg.authorKind === "system" ? conv.unread : conv.unread + 1,
	}
	let next = withConversation(model, msg.conv, bumped)

	if (rootId !== 0) {
		next = appendReply(next, msg, rootId)
		next = patchMessage(next, msg.conv, rootId, bumpRoot(msg))
		// One's own reply is not news to its author: the root's dot follows the same rule as the
		// unread divider two lines below (UX §8 #9).
		if (mine) next = markThreadSeen(next, rootId, msg.seq)
	}
	if (inTimeline) {
		next = appendToWindow(next, msg)
		if (mine) next = anchorOwnMessage(next, msg)
	}
	return { next, result: "applied" }
}

/**
 * One typing signal, under the box it was sent from: the conversation's own, or the reply box of one
 * thread (X-A1 A5). The two are separate maps, so a line never says somebody is writing where they
 * are not.
 */
function applyTyping(
	slice: ChatSlice,
	conv: number,
	rootId: number,
	userId: number,
	at: number,
): ChatSlice {
	if (rootId !== 0) {
		const forThread = { ...(slice.threadTyping[rootId] ?? {}), [userId]: at }
		return { ...slice, threadTyping: { ...slice.threadTyping, [rootId]: forThread } }
	}
	const forConv = { ...(slice.typing[conv] ?? {}), [userId]: at }
	return { ...slice, typing: { ...slice.typing, [conv]: forConv } }
}

/**
 * The module's entry point. The registry selects this reducer by the event's owner prefix
 * (`core/modules/registry.ts`), so inside it the event is one of chat's own.
 */
export function chatReducer(model: Model, wire: WireEventLike): { next: Model; result: Applied } {
	const event = wire as unknown as ChatEvent
	switch (event.type) {
		case "chat.message.new":
			return applyNewMessage(model, event.data)

		case "chat.message.edited": {
			const { data } = event
			return {
				next: patchMessage(model, data.conv, data.id, (msg) => ({
					...msg,
					markdown: data.markdown,
					render: data.render,
					attachments: data.attachments,
					mentions: data.mentions,
					// A collapsed edit — a correction inside the grace window — carries `editedAt: 0`,
					// which is not a timestamp: the row keeps whatever it had.
					...(data.editedAt === 0 ? {} : { editedAt: data.editedAt }),
					version: data.version,
				})),
				result: "applied",
			}
		}

		case "chat.message.deleted": {
			const { data } = event
			const withTombstone = patchMessage(model, data.conv, data.id, (msg) => {
				// A tombstone keeps its place in the sequence and nothing else: no body, no tree,
				// no attachments — the row is what remains, not the message.
				const tombstone: MessageView = {
					...msg,
					markdown: "",
					attachments: [],
					reactions: [],
					deletedAt: data.deletedAt,
					deletedBy: data.deletedBy,
					byAdmin: data.byAdmin,
				}
				delete tombstone.render
				return tombstone
			})
			// The root's counters travel on no event of their own, so a deleted reply takes one off
			// the count wherever the root is loaded — otherwise a footer keeps offering replies that
			// are gone. `lastReplySeq` is left alone: only the thread itself knows what is newest now.
			const root = data.threadRootId ?? 0
			return {
				next:
					root === 0
						? withTombstone
						: patchMessage(withTombstone, data.conv, root, (msg) => ({
								...msg,
								replyCount: Math.max(0, (msg.replyCount ?? 1) - 1),
							})),
				result: "applied",
			}
		}

		case "chat.render.updated": {
			const { data } = event
			return {
				next: patchMessage(model, data.conv, data.id, (msg) => ({ ...msg, render: data.render })),
				result: "applied",
			}
		}

		case "chat.reaction.changed": {
			const { data } = event
			return {
				next: patchMessage(model, data.conv, data.messageId, (msg) => {
					// A chip keeps its place. The server hands a page its chips in the order their first
					// reaction arrived (`selectReactorsOf`), and rebuilding the row as "everything else,
					// then this one" moved the chip a reader had just pressed to the end of the row, under
					// a different finger than the one that was there (N5-R1-11). So a summary that has a
					// chip replaces it where it stands, a new emoji joins at the end — which is the same
					// order a reload answers with — and one that has lost its last reaction goes.
					const current = msg.reactions ?? []
					const at = current.findIndex((r) => r.emoji === data.emoji)
					if (data.summary.count === 0) {
						return at === -1
							? msg
							: { ...msg, reactions: current.filter((r) => r.emoji !== data.emoji) }
					}
					const reactions = [...current]
					if (at === -1) reactions.push(data.summary)
					else reactions[at] = data.summary
					return { ...msg, reactions }
				}),
				result: "applied",
			}
		}

		case "chat.read.state": {
			const { data } = event
			const slice = chatSlice(model)
			let next = withChatSlice(model, {
				...slice,
				cursors: { ...slice.cursors, [data.conv]: data },
			})
			const conv = conversationSlice(model, data.conv)
			if (conv) {
				next = withConversation(next, data.conv, {
					...conv,
					unread: data.unread,
					mentions: data.mentions,
				})
			}
			return { next, result: "applied" }
		}

		case "chat.conversation.created":
			return { next: upsertConversation(model, event.data), result: "applied" }

		case "chat.conversation.updated":
			return {
				next: upsertConversation(model, withReadersAddress(model, event.data)),
				result: "applied",
			}

		case "chat.member.changed": {
			const { data } = event
			const conv = conversationSlice(model, data.conv)
			if (!conv) return { next: model, result: { resync: "all" } }
			const meta = conv.meta as ConversationView
			const ids = new Set(meta.memberIds ?? [])
			for (const member of data.members) {
				if (member.state === "active") ids.add(member.userId)
				else ids.delete(member.userId)
			}
			const nextMeta: ConversationView = {
				...meta,
				memberIds: [...ids],
				memberCount: data.memberCount,
			}
			return {
				next: withConversation(model, data.conv, { ...conv, meta: nextMeta }),
				result: "applied",
			}
		}

		case "chat.membership.self": {
			const { data } = event
			const slice = chatSlice(model)
			const withSelf = withChatSlice(model, {
				...slice,
				self: { ...slice.self, [data.conv]: data.self },
			})
			if (data.self.state !== "active") {
				// A channel that was left or a membership that was ended: the history stays readable
				// where it is public, so the row is kept, but it leaves the reader's own list — the
				// sidebar, the quick switcher and the landing redirect all read that list.
				return {
					next: {
						...withSelf,
						conversationOrder: withSelf.conversationOrder.filter((id) => id !== data.conv),
					},
					result: "applied",
				}
			}
			// An administrator who joined from the outside view: the conversation stops being one, and
			// becomes readable like any other they are in. The event carries the view on a fresh add;
			// should it not, the outside view is the head the conversation moves in with, so the
			// address keeps resolving while the timeline loads.
			const view = data.conversation ?? slice.outside[data.conv]
			const joined = dropOutside(withSelf, data.conv)
			return {
				next: view ? upsertConversation(joined, view) : joined,
				result: "applied",
			}
		}

		// The server mirrors every draft so another device can pick one up, and it tells this one
		// when a mirror moved. Nothing on this screen reads that mirror — the box and the sidebar's
		// pencil read the device's own draft store (core/drafts) — so the two events are accepted
		// and change no model state. They are answered rather than ignored, because an event no case
		// answers is an unknown event, and a second copy of the drafts in the model would be a
		// mirror nobody looks in.
		case "chat.draft.changed":
		case "chat.draft.deleted":
			return { next: model, result: "applied" }

		case "chat.typing": {
			const { data } = event
			return {
				next: withChatSlice(
					model,
					applyTyping(chatSlice(model), data.conv, data.threadRootId ?? 0, data.userId, data.at),
				),
				result: "applied",
			}
		}

		case "chat.presence": {
			const { data } = event
			const slice = chatSlice(model)
			// A frame that says it is the whole picture replaces what is held rather than merging into
			// it (protocol.md §2.5.4): the server sends every person it has an opinion about, and a
			// process that has just started has an opinion about nobody. Merging would leave the green
			// dot of somebody who was here before a restart standing for the life of the tab, because
			// presence is ephemeral and nothing ever replays a correction for them.
			const presence: Record<number, string> = data.full ? {} : { ...slice.presence }
			for (const entry of data.users) presence[entry.userId] = entry.state
			return { next: withChatSlice(model, { ...slice, presence }), result: "applied" }
		}

		case "chat.pin.changed":
			// A pin is not model state: the conversation's pin store (conversation/pins.ts) hears this
			// event through the client tap and keeps the list and a row's marker. The reducer has
			// nothing to write and says so, rather than letting the event count as unknown.
			return { next: model, result: "applied" }

		case "chat.emoji.recents":
			// The recent-emoji list is not model state: the picker keeps it in its own store, which
			// chat's binding feeds from this event (`bind.ts`, I53). The reducer therefore has
			// nothing to write and says so, rather than letting the event count as unknown.
			return { next: model, result: "applied" }

		default: {
			const unhandled: never = event
			void unhandled
			return { next: model, result: "unknown" }
		}
	}
}
